import { useState, useEffect, useCallback } from "react";
import { VpnKey, Edit, OpenInNew, Refresh } from "@mui/icons-material";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Box,
  CircularProgress,
  TextField,
  Alert,
  Paper
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CommonButton from "../../components/CommonButton/CommonButton";
import CopyToClipboardWrapper from "../../components/copyToClipboardWrapper/CopyToClipboardWrapper";
import ForgotPasswordInFirebase from "./ForgotPasswordInFirebase";
import { apiCall } from "../../utils/apiCall";
import {
  BUSINESS_UNLOCK,
  GENERATE_LOGIN_PIN,
  UPDATE_BUSINESS_LOGIN,
  UPDATE_ADMIN_EMAIL
} from "../../utils/url";
import axios from "axios";
import SyncIcon from "../../assets/icons/syncmail.png";

const VpnKeyIcon = VpnKey as any;
const EditIcon = Edit as any;
const apiEndpoint = process.env.REACT_APP_API_BASE_URL;

// Add email validation regex
const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

interface AccountLoginManagementProps {
  businessId: string;
  businessDetails: {
    LoginEmail: string;
    IsLockedOut: number;
    ResetPasswordTokenUrl: string;
    MerchantLoginTokenUrl: string;
    UserId: string;
  };
  merchantId?: string;
  onRefresh?: () => void;
}

const AccountLoginManagementPage: React.FC<AccountLoginManagementProps> = ({
  businessId,
  businessDetails,
  merchantId,
  onRefresh
}) => {
  const [openUnlockModal, setOpenUnlockModal] = useState(false);
  const [unlockLoading, setUnlockLoading] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [newLoginEmail, setNewLoginEmail] = useState("");
  const [emailError, setEmailError] = useState<string>("");
  const [notificationMessage, setNotificationMessage] = useState({
    message: "",
    type: ""
  });
  const [loginPin, setLoginPin] = useState<string>("");
  const [pinLoading, setPinLoading] = useState(false);
  const [openFirebaseResetModal, setOpenFirebaseResetModal] = useState(false);
  const [openMigrateModal, setOpenMigrateModal] = useState(false);
  const [migrateLoading, setMigrateLoading] = useState(false);
  const [openAddBusinessRoleModal, setOpenAddBusinessRoleModal] =
    useState(false);
  const [addBusinessRoleLoading, setAddBusinessRoleLoading] = useState(false);
  const [editNotificationMessage, setEditNotificationMessage] = useState({
    message: "",
    type: ""
  });
  const [openSyncModal, setOpenSyncModal] = useState(false);
  const [syncNotificationMessage, setSyncNotificationMessage] = useState({
    message: "",
    type: ""
  });
  const [openAdminEmailModal, setOpenAdminEmailModal] = useState(false);
  const [adminEmailLoading, setAdminEmailLoading] = useState(false);
  const [newAdminEmail, setNewAdminEmail] = useState("");
  const [adminEmailError, setAdminEmailError] = useState<string>("");
  const [adminEmailNotificationMessage, setAdminEmailNotificationMessage] =
    useState({
      message: "",
      type: ""
    });
  const [comprehensiveData, setComprehensiveData] = useState<any | null>(null);
  const [comprehensiveLoading, setComprehensiveLoading] = useState(false);
  const [comprehensiveError, setComprehensiveError] = useState<string | null>(
    null
  );

  // New state for Firebase status
  const [firebaseStatus, setFirebaseStatus] = useState<{
    isLoading: boolean;
    hasFirebaseId: boolean;
    userId: string | null;
    hasBusinessRole: boolean;
  }>({
    isLoading: true,
    hasFirebaseId: false,
    userId: null,
    hasBusinessRole: false
  });

  const fetchComprehensiveUserData = useCallback(async () => {
    if (!businessId) return;

    setComprehensiveLoading(true);
    setComprehensiveError(null);

    try {
      const response: any = await apiCall(
        `${apiEndpoint}/users/support/comprehensive/business/${businessId}`,
        "GET"
      );

      if (response?.status && response.status >= 400) {
        let errorMessage = "Failed to load comprehensive user data";

        if (
          response?.data?.errors &&
          Array.isArray(response.data.errors) &&
          response.data.errors.length > 0
        ) {
          errorMessage = response.data.errors[0];
        } else if (response?.data?.message) {
          errorMessage = response.data.message;
        } else if (response?.statusText) {
          errorMessage = response.statusText;
        }

        setComprehensiveError(errorMessage);
        setComprehensiveData(null);
        return;
      }

      setComprehensiveData(response || null);
    } catch (error: any) {
      let errorMessage = "Failed to load comprehensive user data";

      if (
        error?.response?.data?.errors &&
        Array.isArray(error.response.data.errors) &&
        error.response.data.errors.length > 0
      ) {
        errorMessage = error.response.data.errors[0];
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      setComprehensiveError(errorMessage);
      setComprehensiveData(null);
    } finally {
      setComprehensiveLoading(false);
    }
  }, [businessId]);

  const checkFirebaseStatus = useCallback(async () => {
    if (!businessId) return;

    setFirebaseStatus((prev) => ({ ...prev, isLoading: true }));
    try {
      if (businessDetails && businessDetails.UserId) {
        const userId = businessDetails.UserId;

        // Now check if user exists in Firebase
        const firebaseResponse: any = await apiCall(
          `${apiEndpoint}/users/support/user/${userId}`,
          "GET"
        );

        const hasFirebaseId = firebaseResponse && firebaseResponse.FirebaseId;
        const hasBusinessRole =
          firebaseResponse &&
          firebaseResponse.Roles &&
          Array.isArray(firebaseResponse.Roles) &&
          firebaseResponse.Roles.includes("Business");

        setFirebaseStatus({
          isLoading: false,
          hasFirebaseId: !!hasFirebaseId,
          userId: userId,
          hasBusinessRole: !!hasBusinessRole
        });
      } else {
        setFirebaseStatus({
          isLoading: false,
          hasFirebaseId: false,
          userId: null,
          hasBusinessRole: false
        });
      }
    } catch (error) {
      console.error("Error checking Firebase status:", error);
      setFirebaseStatus({
        isLoading: false,
        hasFirebaseId: false,
        userId: null,
        hasBusinessRole: false
      });
    }
  }, [businessId, businessDetails]);

  // Check Firebase status when component mounts
  useEffect(() => {
    if (businessId) {
      checkFirebaseStatus();
      fetchComprehensiveUserData();
    }
  }, [businessId, checkFirebaseStatus, fetchComprehensiveUserData]);

  const validateEmail = (email: string) => {
    if (!email) {
      setEmailError("Email is required");
      return false;
    }
    if (!EMAIL_REGEX.test(email)) {
      setEmailError("Please enter a valid email address");
      return false;
    }
    setEmailError("");
    return true;
  };

  const handleUnlockBusiness = async () => {
    if (!businessId) return;

    setUnlockLoading(true);
    try {
      await apiCall(`${apiEndpoint}${BUSINESS_UNLOCK}/${businessId}`, "PUT");
      setNotificationMessage({
        message: "Business unlocked successfully",
        type: "success"
      });
      setOpenUnlockModal(false);
      onRefresh?.();
    } catch (err: any) {
      setNotificationMessage({
        message: err.message || "Failed to unlock business",
        type: "error"
      });
    } finally {
      setUnlockLoading(false);
    }
  };

  const handlePasswordReset = () => {
    if (businessDetails?.ResetPasswordTokenUrl) {
      window.open(businessDetails.ResetPasswordTokenUrl, "_blank");
    }
  };

  const handleMerchantsPortal = () => {
    // if (businessDetails?.MerchantLoginTokenUrl) {
    //   window.open(businessDetails.MerchantLoginTokenUrl, "_blank");
    // }
    window.open(
      `${process.env.REACT_APP_BUSINESS_PORTAL_LINK}?loginwithpin=true`,
      "_blank"
    );
  };

  const handleEditLoginEmail = async (isSyncModal: boolean = false) => {
    if (!businessId || !newLoginEmail) return;

    if (!validateEmail(newLoginEmail)) return;

    setEditLoading(true);

    // Clear any existing modal-specific notification
    if (isSyncModal) {
      setSyncNotificationMessage({
        message: "",
        type: ""
      });
    } else {
      setEditNotificationMessage({
        message: "",
        type: ""
      });
    }

    try {
      const response: any = await apiCall(
        `${apiEndpoint}${UPDATE_BUSINESS_LOGIN}/${businessId}`,
        "PUT",
        {
          Email: newLoginEmail
        }
      );

      debugger;
      // apiCall returns error?.response on error (full axios response object with status property)
      // Check if response has status property (indicating it's an error response)
      const isError = response?.status && response.status >= 400;

      if (isError) {
        let errorMessage = "Failed to update login email";

        // Check for errors array first (as per the API error structure)
        if (
          response?.data?.errors &&
          Array.isArray(response.data.errors) &&
          response.data.errors.length > 0
        ) {
          errorMessage = response.data.errors[0];
        } else if (response?.data?.message) {
          errorMessage = response.data.message;
        } else if (response?.statusText) {
          errorMessage = response.statusText;
        }

        setSyncNotificationMessage({
          message: errorMessage,
          type: "error"
        });
        setEditNotificationMessage({
          message: errorMessage,
          type: "error"
        });
        setEditLoading(false);
        return;
      }

      // Success case - apiCall returns response.data on success (no status property)
      // If response doesn't have status property or status is < 400, it's a success
      setEditLoading(false);
      setEditNotificationMessage({
        message: "Login email updated successfully",
        type: "success"
      });
      if (isSyncModal) {
        // Show success message in sync modal
        setSyncNotificationMessage({
          message: "Email synced successfully",
          type: "success"
        });

        // Close modal and refresh after 2 seconds
        setTimeout(() => {
          setOpenSyncModal(false);
          setNewLoginEmail("");
          setEmailError("");
          setSyncNotificationMessage({
            message: "",
            type: ""
          });
          onRefresh?.();
        }, 2000);
      } else {
        // Close modal and refresh after 2 seconds
        setTimeout(() => {
          setOpenEditModal(false);
          setNewLoginEmail("");
          setEmailError("");
          setEditNotificationMessage({
            message: "",
            type: ""
          });
          onRefresh?.();
        }, 2000);
      }
    } catch (err: any) {
      // Handle any unexpected errors
      let errorMessage = "Failed to update login email";

      if (
        err?.response?.data?.errors &&
        Array.isArray(err.response.data.errors) &&
        err.response.data.errors.length > 0
      ) {
        errorMessage = err.response.data.errors[0];
      } else if (err?.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err?.message) {
        errorMessage = err.message;
      }

      if (isSyncModal) {
        setSyncNotificationMessage({
          message: errorMessage,
          type: "error"
        });
      } else {
        setEditNotificationMessage({
          message: errorMessage,
          type: "error"
        });
      }
      setEditLoading(false);
    }
  };

  const handleOpenEditModal = () => {
    setSyncNotificationMessage({
      message: "",
      type: ""
    });
    setNotificationMessage({
      message: "",
      type: ""
    });
    setEditNotificationMessage({
      message: "",
      type: ""
    });
    if (businessDetails) {
      setNewLoginEmail(businessDetails.LoginEmail);
      setEmailError("");
      setOpenEditModal(true);
    }
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const email = e.target.value;
    setNewLoginEmail(email);
    validateEmail(email);
  };

  const getCurrentAdminEmail = () =>
    comprehensiveData?.merchant?.adminemail ||
    comprehensiveData?.merchant?.adminEmail ||
    "";

  const validateAdminEmail = (email: string) => {
    if (!email) {
      setAdminEmailError("Admin email is required");
      return false;
    }
    if (!EMAIL_REGEX.test(email)) {
      setAdminEmailError("Please enter a valid email address");
      return false;
    }
    if (
      email.trim().toLowerCase() === getCurrentAdminEmail().trim().toLowerCase()
    ) {
      setAdminEmailError("New admin email is same as the current one");
      return false;
    }
    setAdminEmailError("");
    return true;
  };

  const handleAdminEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const email = e.target.value;
    setNewAdminEmail(email);
    validateAdminEmail(email);
  };

  const handleOpenAdminEmailModal = () => {
    setAdminEmailNotificationMessage({ message: "", type: "" });
    setNewAdminEmail("");
    setAdminEmailError("");
    setOpenAdminEmailModal(true);
  };

  const handleUpdateAdminEmail = async () => {
    const currentAdminEmail = getCurrentAdminEmail();

    if (!businessId || !newAdminEmail) return;
    if (!currentAdminEmail) {
      setAdminEmailNotificationMessage({
        message: "Existing admin email not found for this business",
        type: "error"
      });
      return;
    }
    if (!validateAdminEmail(newAdminEmail)) return;

    setAdminEmailLoading(true);
    setAdminEmailNotificationMessage({ message: "", type: "" });

    try {
      const response: any = await apiCall(
        `${apiEndpoint}${UPDATE_ADMIN_EMAIL(businessId)}`,
        "POST",
        {
          OldAdminEmail: currentAdminEmail,
          NewAdminEmail: newAdminEmail
        }
      );

      const isError = response?.status && response.status >= 400;

      if (isError) {
        let errorMessage = "Failed to update admin email";

        if (
          response?.data?.errors &&
          Array.isArray(response.data.errors) &&
          response.data.errors.length > 0
        ) {
          errorMessage = response.data.errors[0];
        } else if (response?.data?.message) {
          errorMessage = response.data.message;
        } else if (response?.statusText) {
          errorMessage = response.statusText;
        }

        setAdminEmailNotificationMessage({
          message: errorMessage,
          type: "error"
        });
        setAdminEmailLoading(false);
        return;
      }

      setAdminEmailLoading(false);
      setAdminEmailNotificationMessage({
        message: "Admin email updated successfully",
        type: "success"
      });

      setTimeout(() => {
        setOpenAdminEmailModal(false);
        setNewAdminEmail("");
        setAdminEmailError("");
        setAdminEmailNotificationMessage({ message: "", type: "" });
        fetchComprehensiveUserData();
        onRefresh?.();
      }, 2000);
    } catch (err: any) {
      let errorMessage = "Failed to update admin email";

      if (
        err?.response?.data?.errors &&
        Array.isArray(err.response.data.errors) &&
        err.response.data.errors.length > 0
      ) {
        errorMessage = err.response.data.errors[0];
      } else if (err?.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err?.message) {
        errorMessage = err.message;
      }

      setAdminEmailNotificationMessage({
        message: errorMessage,
        type: "error"
      });
      setAdminEmailLoading(false);
    }
  };

  const handleGenerateLoginPin = async () => {
    if (!businessId) return;

    setPinLoading(true);
    try {
      const accessToken = localStorage.getItem("accessToken") || "";
      const config: any = {
        method: "post",
        url: `${apiEndpoint}${GENERATE_LOGIN_PIN}/${businessId}`,
        headers: {
          Authorization: `bearer ${accessToken}`,
          "Content-Type": "application/json"
        }
      };
      const response: any = await axios(config);
      setLoginPin(response?.data?.Pin);
      setNotificationMessage({
        message: "Login PIN generated successfully",
        type: "success"
      });
    } catch (err: any) {
      setNotificationMessage({
        message: "Failed to generate login PIN",
        type: "error"
      });
    } finally {
      setPinLoading(false);
    }
  };

  const handleMigrateToFirebase = async () => {
    if (!businessDetails?.UserId) {
      setNotificationMessage({
        message: "User ID not found. Cannot migrate to Firebase.",
        type: "error"
      });
      return;
    }

    setMigrateLoading(true);
    try {
      const response = await apiCall(
        `${apiEndpoint}/users/migrate/business`,
        "POST",
        {
          UserId: businessDetails?.UserId
        }
      );

      if (response) {
        setNotificationMessage({
          message: "Successfully migrated to Firebase!",
          type: "success"
        });
        // Refresh Firebase status
        await checkFirebaseStatus();
        setOpenMigrateModal(false);
      } else {
        throw new Error("Migration failed");
      }
    } catch (error: any) {
      console.error("Migration error:", error);
      setNotificationMessage({
        message:
          error.response?.data?.message ||
          error.message ||
          "Failed to migrate to Firebase. Please try again.",
        type: "error"
      });
    } finally {
      setMigrateLoading(false);
    }
  };

  const handleAddBusinessRole = async () => {
    if (!businessDetails?.UserId || !businessId) {
      setNotificationMessage({
        message: "User ID or Business ID not found. Cannot add Business role.",
        type: "error"
      });
      return;
    }

    setAddBusinessRoleLoading(true);
    try {
      const response = await apiCall(
        `${apiEndpoint}/users/support/user/role`,
        "POST",
        {
          UserId: businessDetails?.UserId,
          Role: "Business",
          BusinessId: businessId,
          BusinessRole: "BusinessAdmin"
        }
      );

      if (response) {
        setNotificationMessage({
          message: "Successfully added Business role!",
          type: "success"
        });
        // Refresh Firebase status
        await checkFirebaseStatus();
        setOpenAddBusinessRoleModal(false);
      } else {
        throw new Error("Failed to add Business role");
      }
    } catch (error: any) {
      console.error("Add Business role error:", error);
      setNotificationMessage({
        message:
          error.response?.data?.message ||
          error.message ||
          "Failed to add Business role. Please try again.",
        type: "error"
      });
    } finally {
      setAddBusinessRoleLoading(false);
    }
  };

  // Helper function to check if all emails match across all systems
  const checkAllEmailsMatch = () => {
    if (!comprehensiveData) return false;

    const emails: string[] = [];

    // Collect all non-null emails
    if (comprehensiveData.merchant?.email) {
      emails.push(
        String(comprehensiveData.merchant.email).toLowerCase().trim()
      );
    }
    if (comprehensiveData.membership?.Email) {
      emails.push(
        String(comprehensiveData.membership.Email).toLowerCase().trim()
      );
    }
    if (comprehensiveData.aspnetUser?.name) {
      emails.push(
        String(comprehensiveData.aspnetUser.name).toLowerCase().trim()
      );
    }
    if (comprehensiveData.monolithAccessTokens?.[0]?.MembershipEmail) {
      emails.push(
        String(comprehensiveData.monolithAccessTokens[0].MembershipEmail)
          .toLowerCase()
          .trim()
      );
    }
    if (comprehensiveData.dynamoDBUser?.Email) {
      emails.push(
        String(comprehensiveData.dynamoDBUser.Email).toLowerCase().trim()
      );
    }
    if (
      Array.isArray(comprehensiveData.firebaseUser)
        ? comprehensiveData.firebaseUser[0]?.Email
        : comprehensiveData.firebaseUser?.Email
    ) {
      emails.push(
        String(
          Array.isArray(comprehensiveData.firebaseUser)
            ? comprehensiveData.firebaseUser[0]?.Email
            : comprehensiveData.firebaseUser?.Email
        )
          .toLowerCase()
          .trim()
      );
    }

    // If no emails found, return false
    if (emails.length === 0) return false;

    // Check if all emails are the same
    const firstEmail = emails[0];
    return emails.every((email) => email === firstEmail);
  };

  // Transform comprehensive data into table rows
  const getTableData = () => {
    if (!comprehensiveData) return [];

    const merchantId =
      comprehensiveData.merchant?.Id || comprehensiveData.merchant?.id;
    const membershipUserId = comprehensiveData.membership?.userId;
    const aspnetUserId =
      comprehensiveData.aspnetUser?.id || comprehensiveData.aspnetUser?.userId;
    const monolithMembershipId =
      comprehensiveData.monolithAccessTokens?.[0]?.MembershipId || "";
    const dynamoDBUserId =
      comprehensiveData.dynamoDBUser?.IdentityUserId ||
      comprehensiveData.dynamoDBUser?.UserId;
    const firebaseId = Array.isArray(comprehensiveData.firebaseUser)
      ? comprehensiveData.firebaseUser[0]?.FirebaseId ||
        comprehensiveData.firebaseUser[0]?.uid
      : comprehensiveData.firebaseUser?.FirebaseId ||
        comprehensiveData.firebaseUser?.uid;

    const merchantEmail = comprehensiveData.merchant?.email;
    const membershipEmail = comprehensiveData.membership?.Email;
    const aspnetUserEmail = comprehensiveData.aspnetUser?.name;
    const monolithEmail =
      comprehensiveData.monolithAccessTokens?.[0]?.MembershipEmail;
    const dynamoDBEmail = comprehensiveData.dynamoDBUser?.Email;
    const firebaseEmail = Array.isArray(comprehensiveData.firebaseUser)
      ? comprehensiveData.firebaseUser[0]?.Email
      : comprehensiveData.firebaseUser?.Email;

    return [
      {
        field: "email",
        merchant: merchantEmail,
        membership: membershipEmail,
        aspnetUser: aspnetUserEmail,
        monolithAccessTokens: monolithEmail,
        dynamoDBUser: dynamoDBEmail,
        firebaseUser: firebaseEmail,
        // Include IDs for copy functionality
        merchantId: merchantId,
        membershipId: membershipUserId,
        aspnetUserId: aspnetUserId,
        monolithId: monolithMembershipId,
        dynamoDBId: dynamoDBUserId,
        firebaseId: firebaseId,
        status: checkAllEmailsMatch()
      }
    ];
  };

  return (
    <div>
      {notificationMessage?.message && (
        <div style={{ marginTop: "10px" }}>
          <Alert severity={notificationMessage?.type as any}>
            {notificationMessage?.message}
          </Alert>
        </div>
      )}

      <div style={{ marginBottom: "20px" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            marginBottom: "10px"
          }}
        >
          <span style={{ fontWeight: "bold" }}>Business ID : </span>{" "}
          <span style={{ marginLeft: "5px" }}>{businessId || "N/A"}</span>
          {businessId && (
            <CopyToClipboardWrapper
              text={businessId || ""}
              cell="Business ID"
            />
          )}
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center"
          }}
        >
          <span style={{ fontWeight: "bold" }}>Merchant ID : </span>{" "}
          <span style={{ marginLeft: "5px" }}>{merchantId || "N/A"}</span>
          {merchantId && (
            <CopyToClipboardWrapper
              text={merchantId || ""}
              cell="Merchant ID"
            />
          )}
        </div>
      </div>

      <div style={{ marginBottom: "20px" }}>
        <h3>Account Status</h3>
        <div style={{ marginTop: "10px" }}>
          <span
            style={{
              color: businessDetails?.IsLockedOut ? "red" : "green",
              marginRight: "10px",
              fontSize: "18px",
              fontWeight: "bold"
            }}
          >
            {businessDetails?.IsLockedOut ? "Locked" : "Not Locked"}
          </span>
          {businessDetails?.IsLockedOut > 0 && (
            <CommonButton
              onClick={() => {
                setNotificationMessage({
                  message: "",
                  type: ""
                });
                setOpenUnlockModal(true);
              }}
            >
              Unlock
            </CommonButton>
          )}
        </div>
      </div>

      <div style={{ marginBottom: "20px" }}>
        <h3>Account credentials</h3>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px"
          }}
        >
          <span>{businessDetails?.LoginEmail}</span>
          <EditIcon
            style={{ cursor: "pointer" }}
            onClick={handleOpenEditModal}
            title="Edit Login Email"
          />
          <VpnKeyIcon
            style={{ cursor: "pointer" }}
            onClick={handlePasswordReset}
            title="Change Password"
          />
        </div>
      </div>

      <div style={{ marginBottom: "20px" }}>
        <h3>Admin Email</h3>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px"
          }}
        >
          <span>
            {comprehensiveData?.merchant?.adminemail ||
              comprehensiveData?.merchant?.adminEmail ||
              "N/A"}
          </span>
          {getCurrentAdminEmail() && (
            <EditIcon
              style={{ cursor: "pointer" }}
              onClick={handleOpenAdminEmailModal}
              title="Edit Admin Email"
            />
          )}
        </div>
      </div>

      <div style={{ marginBottom: "20px" }}>
        <CommonButton
          onClick={handleGenerateLoginPin}
          disabled={pinLoading}
          isLoading={pinLoading}
        >
          {pinLoading ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            "Generate Login Pin"
          )}
        </CommonButton>

        {loginPin && (
          <span
            style={{
              color: "green",
              marginLeft: "10px",
              fontSize: "18px",
              fontWeight: "bold"
            }}
          >
            {loginPin}
          </span>
        )}
      </div>

      <div style={{ marginBottom: "20px" }}>
        <Box>
          <CommonButton onClick={handleMerchantsPortal}>
            <span
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px"
              }}
            >
              Business Portal <OpenInNew />
            </span>
          </CommonButton>
        </Box>
      </div>

      <div style={{ marginTop: "15px" }}>
        {firebaseStatus.isLoading ? (
          <CircularProgress size={20} />
        ) : firebaseStatus.hasFirebaseId ? (
          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
            <CommonButton
              onClick={() => setOpenFirebaseResetModal(true)}
              variant="contained"
              sx={{
                backgroundColor: "#1976d2",
                color: "white",
                "&:hover": {
                  backgroundColor: "#1565c0"
                }
              }}
            >
              Reset Password In Firebase
            </CommonButton>

            {!firebaseStatus.hasBusinessRole && (
              <CommonButton
                onClick={() => setOpenAddBusinessRoleModal(true)}
                variant="contained"
                sx={{
                  backgroundColor: "#28a745",
                  color: "white",
                  "&:hover": {
                    backgroundColor: "#218838"
                  }
                }}
              >
                Add Business Role
              </CommonButton>
            )}
          </div>
        ) : (
          <CommonButton
            onClick={() => setOpenMigrateModal(true)}
            variant="contained"
            sx={{
              backgroundColor: "#223354",
              color: "white",
              "&:hover": {
                backgroundColor: "#1a2a42"
              }
            }}
          >
            Migrate to Firebase
          </CommonButton>
        )}
      </div>

      <div style={{ marginTop: "30px", marginBottom: "20px" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "16px",
            marginBottom: "10px",
            flexWrap: "wrap"
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: "16px" }}>
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: "18px",
                lineHeight: 1.25,
                fontFamily: "Montserrat",
                fontStyle: "normal",
                color: "#192F67"
              }}
              component="h3"
            >
              Business Email Summary
            </Typography>
            {!comprehensiveLoading && !comprehensiveError && comprehensiveData && (
              <>
                <Box
                  sx={{
                    padding: "4px 8px",
                    borderRadius: "4px",
                    backgroundColor: checkAllEmailsMatch()
                      ? "#e8f5e9"
                      : "#ffebee",
                    display: "inline-block"
                  }}
                >
                  <Typography
                    sx={{
                      color: checkAllEmailsMatch() ? "#2e7d32" : "#c62828",
                      fontWeight: 600,
                      fontSize: "12px"
                    }}
                  >
                    {checkAllEmailsMatch() ? "Matched" : "Not Matched"}
                  </Typography>
                </Box>
                {!checkAllEmailsMatch() && (
                  <img
                    src={SyncIcon}
                    alt="Sync Email"
                    onClick={() => {
                      setSyncNotificationMessage({
                        message: "",
                        type: ""
                      });
                      setNewLoginEmail(businessDetails.LoginEmail);
                      setOpenSyncModal(true);
                    }}
                    style={{
                      cursor: "pointer",
                      width: "24px",
                      height: "24px",
                      opacity: 0.7,
                      transition: "opacity 0.2s",
                      marginLeft: "8px"
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.opacity = "1";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.opacity = "0.7";
                    }}
                    title="Sync Email"
                  />
                )}
              </>
            )}
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Refresh
              onClick={fetchComprehensiveUserData}
              sx={{
                cursor: "pointer",
                color: "#192F67",
                "&:hover": { color: "#1565c0" }
              }}
            />
          </Box>
        </Box>

        {comprehensiveLoading && <CircularProgress size={20} />}

        {!comprehensiveLoading && comprehensiveError && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {comprehensiveError}
          </Alert>
        )}

        {!comprehensiveLoading && !comprehensiveError && comprehensiveData && (
          <Box sx={{ marginTop: "16px" }}>
            <Paper sx={{ width: "100%", padding: "20px" }}>
              {(() => {
                const row = getTableData()[0];
                return (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "24px"
                    }}
                  >
                    {/* Monolith Section */}
                    <Box>
                      <Typography
                        sx={{
                          fontWeight: 600,
                          fontSize: "16px",
                          color: "#192f67",
                          fontFamily: "Montserrat, sans-serif",
                          marginBottom: "12px"
                        }}
                      >
                        Monolith
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "6px",
                          paddingLeft: "16px"
                        }}
                      >
                        {/* merchants */}
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            padding: "4px 0"
                          }}
                        >
                          <Typography
                            sx={{
                              fontWeight: 600,
                              fontSize: "14px",
                              color: "#192f67",
                              fontFamily: "Montserrat, sans-serif",
                              minWidth: "180px"
                            }}
                          >
                            merchants:
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                              flex: 1
                            }}
                          >
                            <span style={{ color: "rgba(0, 0, 0, 0.60)" }}>
                              {row.merchant ? row.merchant : "Not Found"}
                            </span>
                            {row.merchant && (
                              <>
                                <CopyToClipboardWrapper
                                  text={row.merchant}
                                  cell="Merchant Email"
                                />
                                {row.merchantId && (
                                  <CopyToClipboardWrapper
                                    text={row.merchantId}
                                    cell="Merchant Id"
                                  />
                                )}
                              </>
                            )}
                          </Box>
                        </Box>

                        {/* aspnet members */}
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            padding: "4px 0"
                          }}
                        >
                          <Typography
                            sx={{
                              fontWeight: 600,
                              fontSize: "14px",
                              color: "#192f67",
                              fontFamily: "Montserrat, sans-serif",
                              minWidth: "180px"
                            }}
                          >
                            aspnet members:
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                              flex: 1
                            }}
                          >
                            <span style={{ color: "rgba(0, 0, 0, 0.60)" }}>
                              {row.membership ? row.membership : "Not Found"}
                            </span>
                            {row.membership && (
                              <>
                                <CopyToClipboardWrapper
                                  text={row.membership}
                                  cell="Membership Email"
                                />
                                {row.membershipId && (
                                  <CopyToClipboardWrapper
                                    text={row.membershipId}
                                    cell="Membership userId"
                                  />
                                )}
                              </>
                            )}
                          </Box>
                        </Box>

                        {/* aspnet user */}
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            padding: "4px 0"
                          }}
                        >
                          <Typography
                            sx={{
                              fontWeight: 600,
                              fontSize: "14px",
                              color: "#192f67",
                              fontFamily: "Montserrat, sans-serif",
                              minWidth: "180px"
                            }}
                          >
                            aspnet user:
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                              flex: 1
                            }}
                          >
                            <span style={{ color: "rgba(0, 0, 0, 0.60)" }}>
                              {row.aspnetUser ? row.aspnetUser : "Not Found"}
                            </span>
                            {row.aspnetUser && (
                              <>
                                <CopyToClipboardWrapper
                                  text={row.aspnetUser}
                                  cell="Aspnet User Email"
                                />
                                {row.aspnetUserId && (
                                  <CopyToClipboardWrapper
                                    text={row.aspnetUserId}
                                    cell="Aspnet User Id"
                                  />
                                )}
                              </>
                            )}
                          </Box>
                        </Box>

                        {/* monolith access tokens */}
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            padding: "4px 0"
                          }}
                        >
                          <Typography
                            sx={{
                              fontWeight: 600,
                              fontSize: "14px",
                              color: "#192f67",
                              fontFamily: "Montserrat, sans-serif",
                              minWidth: "180px"
                            }}
                          >
                            monolith access tokens:
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                              flex: 1
                            }}
                          >
                            <span style={{ color: "rgba(0, 0, 0, 0.60)" }}>
                              {row.monolithAccessTokens
                                ? row.monolithAccessTokens
                                : "Not Found"}
                            </span>
                            {row.monolithAccessTokens && (
                              <>
                                <CopyToClipboardWrapper
                                  text={row.monolithAccessTokens}
                                  cell="MonolithAccessToken Email"
                                />
                                {row.monolithId && (
                                  <CopyToClipboardWrapper
                                    text={row.monolithId}
                                    cell="MonolithAccessToken MembershipId"
                                  />
                                )}
                              </>
                            )}
                          </Box>
                        </Box>
                      </Box>
                    </Box>

                    {/* User Service Section */}
                    <Box>
                      <Typography
                        sx={{
                          fontWeight: 600,
                          fontSize: "16px",
                          color: "#192f67",
                          fontFamily: "Montserrat, sans-serif",
                          marginBottom: "12px"
                        }}
                      >
                        User Service
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "6px",
                          paddingLeft: "16px"
                        }}
                      >
                        {/* users */}
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            padding: "4px 0"
                          }}
                        >
                          <Typography
                            sx={{
                              fontWeight: 600,
                              fontSize: "14px",
                              color: "#192f67",
                              fontFamily: "Montserrat, sans-serif",
                              minWidth: "180px"
                            }}
                          >
                            users:
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                              flex: 1
                            }}
                          >
                            <span style={{ color: "rgba(0, 0, 0, 0.60)" }}>
                              {row.dynamoDBUser
                                ? row.dynamoDBUser
                                : "Not Found"}
                            </span>
                            {row.dynamoDBUser && (
                              <>
                                <CopyToClipboardWrapper
                                  text={row.dynamoDBUser}
                                  cell="DynamoDB Email"
                                />
                                {row.dynamoDBId && (
                                  <CopyToClipboardWrapper
                                    text={row.dynamoDBId}
                                    cell="DynamoDB IdentityUserId"
                                  />
                                )}
                              </>
                            )}
                          </Box>
                        </Box>
                      </Box>
                    </Box>

                    {/* Firebase Section */}
                    <Box>
                      <Typography
                        sx={{
                          fontWeight: 600,
                          fontSize: "16px",
                          color: "#192f67",
                          fontFamily: "Montserrat, sans-serif",
                          marginBottom: "12px"
                        }}
                      >
                        Firebase
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "6px",
                          paddingLeft: "16px"
                        }}
                      >
                        {/* authentication */}
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            padding: "4px 0"
                          }}
                        >
                          <Typography
                            sx={{
                              fontWeight: 600,
                              fontSize: "14px",
                              color: "#192f67",
                              fontFamily: "Montserrat, sans-serif",
                              minWidth: "180px"
                            }}
                          >
                            authentication:
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                              flex: 1
                            }}
                          >
                            <span style={{ color: "rgba(0, 0, 0, 0.60)" }}>
                              {row.firebaseUser
                                ? row.firebaseUser
                                : "Not Found"}
                            </span>
                            {row.firebaseUser && (
                              <>
                                <CopyToClipboardWrapper
                                  text={row.firebaseUser}
                                  cell="Firebase Email"
                                />
                                {row.firebaseId && (
                                  <CopyToClipboardWrapper
                                    text={row.firebaseId}
                                    cell="Firebase Id"
                                  />
                                )}
                              </>
                            )}
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                );
              })()}
            </Paper>
          </Box>
        )}

        {!comprehensiveLoading && !comprehensiveError && !comprehensiveData && (
          <Typography variant="body2" color="text.secondary">
            No comprehensive user data found for this business.
          </Typography>
        )}
      </div>

      {/* Unlock Confirmation Modal */}
      <Dialog
        open={openUnlockModal}
        onClose={() => !unlockLoading && setOpenUnlockModal(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: "18px",
                lineHeight: 1.25,
                fontFamily: "Montserrat",
                fontStyle: "normal",
                color: "#192F67"
              }}
            >
              Confirm Unlock
            </Typography>
            <IconButton
              aria-label="close"
              onClick={() => !unlockLoading && setOpenUnlockModal(false)}
              disabled={unlockLoading}
              sx={{
                color: (theme) =>
                  unlockLoading
                    ? theme.palette.grey[400]
                    : theme.palette.grey[500]
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Typography>
            Are you sure you want to unlock this business account? This will
            allow the user to log in again.
          </Typography>
          {notificationMessage?.message && (
            <div style={{ marginTop: "10px" }}>
              <Alert severity={notificationMessage?.type as any}>
                {notificationMessage?.message}
              </Alert>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <CommonButton
            onClick={() => !unlockLoading && setOpenUnlockModal(false)}
            disabled={unlockLoading}
            variant="contained"
          >
            Cancel
          </CommonButton>

          <CommonButton
            onClick={handleUnlockBusiness}
            disabled={unlockLoading}
            isLoading={unlockLoading}
            variant="contained"
            sx={{ minHeight: "36px" }}
          >
            {unlockLoading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              "Unlock"
            )}
          </CommonButton>
        </DialogActions>
      </Dialog>

      {/* Edit Login Email Modal */}
      <Dialog
        open={openEditModal}
        onClose={() => !editLoading && setOpenEditModal(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: "18px",
                lineHeight: 1.25,
                fontFamily: "Montserrat",
                fontStyle: "normal",
                color: "#192F67"
              }}
            >
              Edit Login Email
            </Typography>
            <IconButton
              aria-label="close"
              onClick={() => !editLoading && setOpenEditModal(false)}
              disabled={editLoading}
              sx={{
                color: (theme) =>
                  editLoading
                    ? theme.palette.grey[400]
                    : theme.palette.grey[500]
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            fullWidth
            label="Login Email"
            value={newLoginEmail}
            onChange={handleEmailChange}
            disabled={editLoading}
            error={!!emailError}
            helperText={emailError}
            sx={{ mt: editNotificationMessage?.message ? 0 : 1 }}
          />

          {editNotificationMessage?.message && (
            <Box sx={{ mt: 2 }}>
              <Alert
                severity={
                  editNotificationMessage?.type as
                    | "success"
                    | "error"
                    | "info"
                    | "warning"
                }
              >
                {editNotificationMessage?.message}
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <CommonButton
            onClick={() => !editLoading && setOpenEditModal(false)}
            disabled={editLoading}
            variant="contained"
          >
            Cancel
          </CommonButton>
          <CommonButton
            onClick={handleEditLoginEmail}
            disabled={editLoading || !newLoginEmail || !!emailError}
            isLoading={editLoading}
            variant="contained"
            sx={{ minHeight: "36px" }}
          >
            {editLoading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              "Save"
            )}
          </CommonButton>
        </DialogActions>
      </Dialog>

      {/* Edit Admin Email Modal */}
      <Dialog
        open={openAdminEmailModal}
        onClose={() => !adminEmailLoading && setOpenAdminEmailModal(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: "18px",
                lineHeight: 1.25,
                fontFamily: "Montserrat",
                fontStyle: "normal",
                color: "#192F67"
              }}
            >
              Edit Admin Email
            </Typography>
            <IconButton
              aria-label="close"
              onClick={() =>
                !adminEmailLoading && setOpenAdminEmailModal(false)
              }
              disabled={adminEmailLoading}
              sx={{
                color: (theme) =>
                  adminEmailLoading
                    ? theme.palette.grey[400]
                    : theme.palette.grey[500]
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            fullWidth
            label="Current Admin Email"
            value={getCurrentAdminEmail()}
            disabled
            sx={{ mb: 2, mt: 2 }}
          />
          <TextField
            fullWidth
            label="New Admin Email"
            value={newAdminEmail}
            onChange={handleAdminEmailChange}
            disabled={adminEmailLoading}
            error={!!adminEmailError}
            helperText={adminEmailError}
          />

          {adminEmailNotificationMessage?.message && (
            <Box sx={{ mt: 2 }}>
              <Alert
                severity={
                  adminEmailNotificationMessage?.type as
                    | "success"
                    | "error"
                    | "info"
                    | "warning"
                }
              >
                {adminEmailNotificationMessage?.message}
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <CommonButton
            onClick={() => !adminEmailLoading && setOpenAdminEmailModal(false)}
            disabled={adminEmailLoading}
            variant="contained"
          >
            Cancel
          </CommonButton>
          <CommonButton
            onClick={handleUpdateAdminEmail}
            disabled={adminEmailLoading || !newAdminEmail || !!adminEmailError}
            isLoading={adminEmailLoading}
            variant="contained"
            sx={{ minHeight: "36px" }}
          >
            {adminEmailLoading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              "Save"
            )}
          </CommonButton>
        </DialogActions>
      </Dialog>

      {/* Firebase Password Reset Modal */}
      <ForgotPasswordInFirebase
        open={openFirebaseResetModal}
        onClose={() => setOpenFirebaseResetModal(false)}
        businessId={businessId}
        businessEmail={businessDetails?.LoginEmail || ""}
        setParrentNotificationMessage={setNotificationMessage}
      />

      {/* Migrate to Firebase Confirmation Modal */}
      <Dialog
        open={openMigrateModal}
        onClose={() => !migrateLoading && setOpenMigrateModal(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: "18px",
                lineHeight: 1.25,
                fontFamily: "Montserrat",
                fontStyle: "normal",
                color: "#192F67"
              }}
            >
              Confirm Migration to Firebase
            </Typography>
            <IconButton
              aria-label="close"
              onClick={() => !migrateLoading && setOpenMigrateModal(false)}
              disabled={migrateLoading}
              sx={{
                color: (theme) =>
                  migrateLoading
                    ? theme.palette.grey[400]
                    : theme.palette.grey[500]
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Typography>
            Are you sure you want to migrate this user to Firebase? This action
            will migrate the user account to Firebase authentication system.
          </Typography>
          {notificationMessage?.message && (
            <div style={{ marginTop: "10px" }}>
              <Alert severity={notificationMessage?.type as any}>
                {notificationMessage?.message}
              </Alert>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <CommonButton
            onClick={() => !migrateLoading && setOpenMigrateModal(false)}
            disabled={migrateLoading}
            variant="contained"
          >
            Cancel
          </CommonButton>
          <CommonButton
            onClick={handleMigrateToFirebase}
            disabled={migrateLoading}
            isLoading={migrateLoading}
            variant="contained"
            sx={{
              backgroundColor: "#223354",
              color: "white",
              "&:hover": {
                backgroundColor: "#1a2a42"
              },
              minHeight: "36px"
            }}
          >
            {migrateLoading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              "Migrate"
            )}
          </CommonButton>
        </DialogActions>
      </Dialog>

      {/* Add Business Role Confirmation Modal */}
      <Dialog
        open={openAddBusinessRoleModal}
        onClose={() =>
          !addBusinessRoleLoading && setOpenAddBusinessRoleModal(false)
        }
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: "18px",
                lineHeight: 1.25,
                fontFamily: "Montserrat",
                fontStyle: "normal",
                color: "#192F67"
              }}
            >
              Confirm Add Business Role
            </Typography>
            <IconButton
              aria-label="close"
              onClick={() =>
                !addBusinessRoleLoading && setOpenAddBusinessRoleModal(false)
              }
              disabled={addBusinessRoleLoading}
              sx={{
                color: (theme) =>
                  addBusinessRoleLoading
                    ? theme.palette.grey[400]
                    : theme.palette.grey[500]
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Typography>
            Are you sure you want to add the Business role to this user? This
            will grant the user access to business-related features.
          </Typography>
          {notificationMessage?.message && (
            <div style={{ marginTop: "10px" }}>
              <Alert severity={notificationMessage?.type as any}>
                {notificationMessage?.message}
              </Alert>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <CommonButton
            onClick={() =>
              !addBusinessRoleLoading && setOpenAddBusinessRoleModal(false)
            }
            disabled={addBusinessRoleLoading}
            variant="contained"
          >
            Cancel
          </CommonButton>
          <CommonButton
            onClick={handleAddBusinessRole}
            disabled={addBusinessRoleLoading}
            isLoading={addBusinessRoleLoading}
            variant="contained"
            sx={{
              backgroundColor: "#28a745",
              color: "white",
              "&:hover": {
                backgroundColor: "#218838"
              },
              minHeight: "36px"
            }}
          >
            {addBusinessRoleLoading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              "Add Business Role"
            )}
          </CommonButton>
        </DialogActions>
      </Dialog>

      {/* Sync Email Confirmation Modal */}
      <Dialog
        open={openSyncModal}
        onClose={() => !editLoading && setOpenSyncModal(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: "18px",
                lineHeight: 1.25,
                fontFamily: "Montserrat",
                fontStyle: "normal",
                color: "#192F67"
              }}
            >
              Confirm Sync Email
            </Typography>
            <IconButton
              aria-label="close"
              onClick={() => !editLoading && setOpenSyncModal(false)}
              disabled={editLoading}
              sx={{
                color: (theme) =>
                  editLoading
                    ? theme.palette.grey[400]
                    : theme.palette.grey[500]
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Typography>Are you sure you want to sync the email?.</Typography>
          {syncNotificationMessage?.message && (
            <Box sx={{ mt: 2 }}>
              <Alert
                severity={
                  syncNotificationMessage?.type as
                    | "success"
                    | "error"
                    | "info"
                    | "warning"
                }
              >
                {syncNotificationMessage?.message}
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <CommonButton
            onClick={() => !editLoading && setOpenSyncModal(false)}
            disabled={editLoading}
            variant="contained"
          >
            Cancel
          </CommonButton>
          <CommonButton
            onClick={() => handleEditLoginEmail(true)}
            disabled={editLoading || !newLoginEmail || !!emailError}
            isLoading={editLoading}
            variant="contained"
            sx={{ minHeight: "36px" }}
          >
            {editLoading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              "Sync"
            )}
          </CommonButton>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default AccountLoginManagementPage;
